import { useEffect, useState, useCallback } from "react";
import {
  Scale, Users2, Gauge, Sparkles, AlarmClock, ListTodo, ArrowUpDown,
  Download, Loader2, X, UserPlus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Cell, ReferenceLine,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import DateRangePicker from "@/components/DateRangePicker";
import { toast } from "sonner";

const fairTone = (label) => ({ "Balanced": "text-emerald-600", "Slightly imbalanced": "text-amber-500", "Imbalanced": "text-red-500" }[label] || "text-ink/40");
const statusStyle = { "Overloaded": "bg-red-100 text-red-700", "Underloaded": "bg-blue-100 text-blue-700", "Balanced": "bg-emerald-100 text-emerald-700" };
const QUICK = [["", "All"], ["overloaded", "Overloaded"], ["underloaded", "Underloaded"], ["high_overdue", "High overdue"], ["high_pending", "High pending"]];

function ChartCard({ title, children, empty, refLine }) {
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid={`chart-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h3 className="mb-3 flex items-center justify-between font-sora text-sm font-semibold text-navy">{title}{refLine != null && <span className="text-[11px] font-normal text-ink/45">Fair share · {refLine}</span>}</h3>
      {empty ? <div className="flex h-48 items-center justify-center text-xs text-ink/40">No data for the selected filters.</div> : <div className="h-48">{children}</div>}
    </div>
  );
}
const SimpleBar = ({ data, color = "#2F6BFF", refLine }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={55} />
      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
      <RTooltip />
      {refLine != null && refLine > 0 && <ReferenceLine y={refLine} stroke="#f59e0b" strokeDasharray="4 4" />}
      <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((e, i) => <Cell key={i} fill={color} />)}</Bar>
    </BarChart>
  </ResponsiveContainer>
);

export default function WorkloadAuditReport() {
  const { user, options } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [filters, setFilters] = useState({ created_from: "", created_to: "", manager: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" });
  const [opts, setOpts] = useState({ salespersons: [], sources: [], campaigns: [], services: [], managers: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quick, setQuick] = useState("");
  const [sort, setSort] = useState({ key: "active", dir: "desc" });
  const [exporting, setExporting] = useState(false);

  const clean = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  useEffect(() => { api.get("/reports/filter-options").then(({ data }) => setOpts(data.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await minDelay(api.get("/reports/workload-audit", { params: clean() })); setData(data.data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ created_from: "", created_to: "", manager: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" }); setQuick(""); };
  const hasFilters = Object.values(filters).some(Boolean) || quick;
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  // Dynamic Workload/Capacity engine (Phase 2b) — additive, behind a toggle.
  const [dynOn, setDynOn] = useState(false);
  const [dynMap, setDynMap] = useState({});
  const [dynCfg, setDynCfg] = useState(null);
  useEffect(() => {
    if (!dynOn) return;
    api.get("/scoring/compute-batch", { params: { entity_type: "workload", ...clean() } })
      .then(({ data }) => { setDynCfg(data.data.config); const m = {}; (data.data.rows || []).forEach((x) => { m[x.entity_id] = x; }); setDynMap(m); })
      .catch(() => setDynMap({}));
  }, [dynOn, filters]); // eslint-disable-line
  const dynTip = (b) => (b || []).map((x) => `${x.label}: ${x.available ? x.score : "no data"} (${x.weight}%)`).join("\n");

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/workload-audit/export", { params: clean(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "workload_audit.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Workload audit exported");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  if (loading && !data) return <Loading label="Loading workload audit..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const empty = !data || data.empty;
  const k = data?.kpis, c = data?.charts;
  const expected = data?.expected;
  let rows = data?.rows || [];
  if (quick === "overloaded") rows = rows.filter((r) => r.status === "Overloaded");
  else if (quick === "underloaded") rows = rows.filter((r) => r.status === "Underloaded");
  else if (quick === "high_overdue") rows = rows.filter((r) => r.overdue >= 5);
  else if (quick === "high_pending") rows = rows.filter((r) => r.pending >= 10);
  rows = [...rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    const cmp = sort.key === "name" ? String(x).localeCompare(String(y)) : ((Number(x) || 0) - (Number(y) || 0));
    return sort.dir === "desc" ? -cmp : cmp;
  });

  const cards = empty ? [] : [
    { label: "Active Leads", value: k.total_active, icon: ListTodo, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Active Reps", value: k.active_reps, icon: Users2, tone: "text-navy", bg: "bg-mist" },
    { label: "Fair Share / Rep", value: k.expected_per_rep, icon: Scale, tone: "text-purple-500", bg: "bg-purple-50" },
    { label: "Workload Fairness", value: k.workload_fairness, sub: k.workload_label, icon: Gauge, tone: fairTone(k.workload_label), bg: "bg-mist", big: true },
    { label: "Quality Fairness", value: k.quality_fairness, sub: k.quality_label, icon: Sparkles, tone: fairTone(k.quality_label), bg: "bg-emerald-50", big: true },
    { label: "Overloaded Reps", value: k.overloaded, icon: AlarmClock, tone: "text-red-500", bg: "bg-red-50" },
    { label: "Underloaded Reps", value: k.underloaded, icon: Users2, tone: "text-blue-500", bg: "bg-blue-50" },
    ...(k.unassigned_active != null ? [{ label: "Unassigned Active", value: k.unassigned_active, icon: UserPlus, tone: "text-orange-500", bg: "bg-orange-50" }] : []),
  ];

  const COLS = [
    { key: "name", label: "Rep", align: "left" },
    { key: "manager", label: "Manager", align: "left", text: true },
    { key: "assigned", label: "Assigned" },
    { key: "active", label: "Active" },
    { key: "share_pct", label: "Share %", suffix: "%" },
    { key: "expected", label: "Fair Share" },
    { key: "deviation", label: "Deviation", dev: true },
    { key: "status", label: "Status", badge: true },
    { key: "ace_king_active", label: "Active Ace+King" },
    { key: "contacted_pct", label: "Contacted %", suffix: "%" },
    { key: "pending", label: "Pending" },
    { key: "overdue", label: "Overdue" },
  ];

  return (
    <div className="space-y-6" data-testid="workload-audit-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Scale className="h-7 w-7 text-loop" />Assignment & Workload Audit</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">Is open work — and quality leads — distributed fairly across the team?{loading && data && <span className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}</p>
        </div>
        {isAdmin && !empty && (
          <button onClick={doExport} disabled={exporting} data-testid="wl-export-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-sky px-3 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky/70 bg-white p-3" data-testid="wl-filters">
        <DateRangePicker from={filters.created_from} to={filters.created_to} onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))} testid="wl-date" />
        {isAdmin && (
          <select value={filters.manager} onChange={(e) => setF("manager", e.target.value)} data-testid="f-manager" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All managers</option>{opts.managers?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        )}
        <select value={filters.salesperson} onChange={(e) => setF("salesperson", e.target.value)} data-testid="f-salesperson" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All salespersons</option>{opts.salespersons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={filters.source} onChange={(e) => setF("source", e.target.value)} data-testid="f-source" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All sources</option>{opts.sources.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.campaign} onChange={(e) => setF("campaign", e.target.value)} data-testid="f-campaign" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All campaigns</option>{opts.campaigns.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.stage} onChange={(e) => setF("stage", e.target.value)} data-testid="f-stage" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All stages</option>{(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}</select>
        <select value={filters.quality} onChange={(e) => setF("quality", e.target.value)} data-testid="f-quality" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All quality</option>{(options?.qualities || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.service} onChange={(e) => setF("service", e.target.value)} data-testid="f-service" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All services</option>{opts.services.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        {hasFilters && <button onClick={clearFilters} data-testid="clear-filters" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist"><X className="h-3.5 w-3.5" />Clear</button>}
      </div>

      {empty ? (
        <EmptyState title="No report data found for the selected filters." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="wl-kpis">
            {cards.map((t) => (
              <div key={t.label} className="rounded-2xl border border-sky/70 bg-white p-4">
                <div className={`mb-2 inline-flex rounded-lg p-2 ${t.bg}`}><t.icon className={`h-4 w-4 ${t.tone}`} /></div>
                <p className={`font-sora font-bold text-navy ${t.big ? `text-xl ${t.tone}` : "text-2xl"}`}>{t.value}</p>
                <p className="text-xs text-ink/55">{t.label}</p>
                {t.big && t.sub && <p className={`mt-0.5 text-xs font-semibold ${t.tone}`} data-testid="wl-fairness-label">{t.sub}</p>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title="Active Leads by Rep" empty={!c.active_by_rep.length} refLine={expected}><SimpleBar data={c.active_by_rep} refLine={expected} /></ChartCard>
            <ChartCard title="Active Ace+King by Rep" empty={c.ace_king_by_rep.every((x) => !x.value)}><SimpleBar data={c.ace_king_by_rep} color="#10b981" /></ChartCard>
            <ChartCard title="Overdue Backlog by Rep" empty={c.overdue_by_rep.every((x) => !x.value)}><SimpleBar data={c.overdue_by_rep} color="#ef4444" /></ChartCard>
            <ChartCard title="Pending Activities by Rep" empty={c.pending_by_rep.every((x) => !x.value)}><SimpleBar data={c.pending_by_rep} color="#f59e0b" /></ChartCard>
          </div>
        </>
      )}

      {!empty && (
        <div className="rounded-2xl border border-sky/70 bg-white" data-testid="wl-table">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-sky/60 p-3">
            {QUICK.map(([v, l]) => <button key={v} onClick={() => setQuick(v)} data-testid={`quick-${v || "all"}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${quick === v ? "bg-loop text-white" : "border border-sky text-ink/60 hover:bg-mist"}`}>{l}</button>)}
            <button onClick={() => setDynOn((v) => !v)} data-testid="wl-dyn-toggle" title={dynCfg ? `${dynCfg.name} v${dynCfg.version}` : "Use the Admin-configured dynamic capacity engine"}
              className={`ml-auto h-8 rounded-lg px-3 text-xs font-semibold transition-colors ${dynOn ? "bg-loop text-white" : "border border-loop/40 text-loop hover:bg-loop/5"}`}>
              {dynOn ? `Dynamic engine: ON${dynCfg ? ` · v${dynCfg.version}` : ""}` : "Dynamic engine (beta)"}
            </button>
          </div>
          {!rows.length ? <EmptyState title="No reps match this filter." /> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-mist/70 text-left text-ink/50"><tr>
                  {COLS.map((col) => (
                    <th key={col.key} onClick={() => toggleSort(col.key)} data-testid={`wl-sort-${col.key}`} className="cursor-pointer whitespace-nowrap px-3 py-2 font-semibold hover:text-loop">
                      <span className="inline-flex items-center gap-1">{col.label}<ArrowUpDown className={`h-3 w-3 ${sort.key === col.key ? "text-loop" : "text-ink/30"}`} /></span>
                    </th>
                  ))}
                  {dynOn && ["Capacity Score", "Status / Diagnosis"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-sky/40">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-sky/10" data-testid={`wl-row-${r.id}`}>
                      {COLS.map((col) => (
                        <td key={col.key} className={`whitespace-nowrap px-3 py-2 ${col.align === "left" && !col.text ? "font-medium text-navy" : "text-ink/70"} ${col.dev ? (r.deviation > 0 ? "font-semibold text-red-500" : r.deviation < 0 ? "font-semibold text-blue-500" : "") : ""}`}>
                          {col.badge ? <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${statusStyle[r.status] || "bg-mist text-ink/60"}`}>{r.status}</span>
                            : col.dev ? (r.deviation > 0 ? `+${r.deviation}` : r.deviation)
                              : `${r[col.key]}${col.suffix || ""}`}
                        </td>
                      ))}
                      {dynOn && (() => { const d = dynMap[r.id]; return (
                        <>
                          <td className="whitespace-nowrap px-3 py-2 font-bold text-navy" title={d ? dynTip(d.breakdown) : "No dynamic score"} data-testid={`wl-dyn-${r.id}`}>
                            <span className="cursor-help">{d?.insufficient || d?.score == null ? "—" : d.score}</span>
                            <span className="block text-[10px] font-medium text-ink/45">{d?.label || "—"}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {(d?.flags || []).slice(0, 3).map((f) => (
                                <span key={f.key} title={`${f.diagnosis || ""}${f.recommended_action ? "\n→ " + f.recommended_action : ""}`}
                                  className={`cursor-help rounded px-1.5 py-0.5 text-[10px] font-semibold ${f.severity === "critical" ? "bg-red-50 text-red-600" : f.severity === "warning" ? "bg-amber-50 text-amber-600" : f.severity === "positive" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{f.label}</span>
                              ))}
                              {!(d?.flags || []).length && <span className="text-[10px] text-ink/30">—</span>}
                            </div>
                          </td>
                        </>
                      ); })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
